import React from 'react';

//Hooks
import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

//Actions
import * as actions from '../../../../store/actions';

//Components
import { Container, Row, Button } from 'react-bootstrap';
import { XCircle } from 'react-feather';
import Col from '../../../../components/Col/Col';
import { Redirect } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import ScreenHeader from '../../../../components/ScreenHeader/ScreenHeader';
import Service from '../../../../components/Service/Service';
import Product from '../../../../components/Product/Product';
import Calendar from '../../../../components/Calendar/Calendar';
import ScreenFooter from '../../../../components/ScreenFooter/ScreenFooter';

//Styles
import Styles from './Booking.module.scss';

//Utils
import moment from 'moment';
import { scrollTo } from '../../../../utility/scroll';
import { ROUTER_URL } from '../../../../const/ROUTER_URL';

const Booking = () => {
    const shopId = useSelector(state => state.companies.shopId);
    const booking = useSelector(state => {
        let booking = {};
        if (state.booking.serviceCategories && state.booking.selectedServiceID) {
            Object.values(state.booking.serviceCategories).some((category) => {
                return category.some((service) => {
                    if (service.sub_options.length) {
                        return service.sub_options.some((subService) => {
                            const isSelected = subService.id === state.booking.selectedServiceID;
                            if (isSelected) {
                                booking = {
                                    ...subService,
                                    hide_price: service.hide_price,
                                };
                            }
                            return isSelected;
                        })
                    } else {
                        const isSelected = service.id === state.booking.selectedServiceID;
                        if (isSelected) {
                            booking = service;
                        }
                        return isSelected;
                    }
                })
            })
        }
        return {
            ...booking,
            isHidePrice: booking.hide_price,
        };
    });
    const products = useSelector(state => state.booking.productList ? state.booking.productList.sort((a, b) => String(a.id).localeCompare(b.id)) : null);
    const productsError = useSelector(state => state.booking.productListError);
    const availableTimeList = useSelector(state => state.booking.availableTimeList);
    const selectedProductID = useSelector(state => state.booking.selectedProduct ? state.booking.selectedProduct.id : null);
    const selectedHour = useSelector(state => state.booking.selectedTimestamp);
    const dispatch = useDispatch();

    const [month, setMonth] = useState(moment().month());
    const [year, setYear] = useState(moment().year());
    const [selectedDate, setSelectedDate] = useState(null);
    const style = useSelector(state => state.widget.style);

    const calendar = useRef(null);
    const hours = useRef(null);
    const productList = useRef(null);

    const { push } = useHistory();
    const { t } = useTranslation('common');

    const id = booking ? booking.id : null;

    useEffect(() => {
        if (!id) {
            return;
        }

        dispatch(actions.getProductList(id, 'single'));
        dispatch(actions.selectProduct(null));
        dispatch(actions.getAvailableTimeSucceeded(null));
    }, [dispatch, id]);

    useEffect(() => {
        if (!products || products.length !== 1) {
            return;
        }
        dispatch(actions.selectProduct(products[0]));
    }, [products, dispatch]);

    useEffect(() => {
        if (!id || !selectedDate) {
            return;
        }
        dispatch(actions.getAvailableTime(id, selectedProductID, selectedDate.unix()));
    }, [dispatch, id, selectedProductID, selectedDate]);

    useEffect(() => {
        dispatch(actions.selectTimestamp(null));

        if (availableTimeList && selectedDate && hours.current) {
            scrollTo(hours);
        }
    }, [availableTimeList, selectedDate, dispatch]);

    useEffect(() => {
        if (!products) {
            return;
        }

        const product = products.find(({ id }) => id === selectedProductID);
        if (!product || !selectedDate || (!product?.disabled_dates.find(date => moment(date).isSame(selectedDate)) && product?.disabled_days.indexOf(selectedDate.isoWeekday() - 1) === -1)) {
            return;
        }

        setSelectedDate(null);
    }, [dispatch, selectedProductID, products, selectedDate]);

    const handleScroll = /* istanbul ignore next */ ({ deltaY }) => productList.current.scrollBy(deltaY, 0);

    useEffect(() => {
        if (!products || products.length === 1 || !productList.current) {
            return;
        }

        const productListCurrent = productList.current;

        productListCurrent.addEventListener('wheel', handleScroll);
        return /* istanbul ignore next */ () => {
            if (!productListCurrent) {
                return;
            }

            productListCurrent.removeEventListener('wheel', handleScroll)
        };
    }, [productList, products]);

    if (!booking) {
        return <Redirect to={ROUTER_URL.SHOPS_ID({ shopId })} />
    }

    const disabled_days = selectedProductID !== null && products ? products.find(({ id }) => id === selectedProductID)?.disabled_days : [];
    const disabledDates = selectedProductID !== null && products ? products.find(({ id }) => id === selectedProductID)?.disabled_dates.map(date => moment(date)) : [];
    const futureBooking = selectedProductID !== null && products && products.find(({ id }) => id === selectedProductID)?.future_booking;

    return (
        <>
            <ScreenHeader>
                <h2 className={Styles.title} data-testid='data-test-bookAVisit-title'>
                    {t('homeScreen.bookAVisit')}
                </h2>
            </ScreenHeader>

            <div
                className={Styles.container}
                style={{ paddingBottom: (style === 'dev' || style === 'website') && !!selectedHour ? '150px' : '40px' }}
            >
                <Container className={'flex-grow-1 d-flex flex-column'}>
                    {!productsError && (
                        <Row>
                            <Col>
                                <Service
                                    service={booking}
                                    showCheckIcon={false}
                                />
                            </Col>
                        </Row>
                    )}

                    {!productsError && (!products || (products && products.length !== 1)) && (
                        <Row>
                            <Col>
                                {!products && (
                                    <Loader
                                        className="d-block text-center"
                                        type="TailSpin"
                                        color="#000"
                                        height={32}
                                        width={32}
                                    />
                                )}

                                {products && (
                                    <>
                                        <div ref={productList} className={Styles.products}>
                                            {products.map(product => <Product key={`product-${product.id}`}
                                                                              product={product}
                                                                              selected={selectedProductID !== null && product.id === selectedProductID}
                                                                              onClick={() => {
                                                                                  dispatch(actions.selectProduct(product));
                                                                                  scrollTo(calendar);
                                                                              }}/>)}
                                        </div>
                                    </>
                                )}
                            </Col>
                        </Row>
                    )}

                    {productsError && (
                        <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center">
                            <XCircle size={64} color={'red'}/>
                            <p className={Styles.error}>{productsError}</p>
                        </div>
                    )}

                    <Row>
                        <Col xs={12} md={6} className={'mt-4'}>
                            {(!productsError && selectedProductID !== null) && (
                                <>
                                    <span className={Styles.label}>
                                        {t('bookingScreen.chooseADate')}
                                    </span>
                                    <Calendar
                                        data-testid={'calendar'}
                                        ref={calendar}
                                        month={month}
                                        year={year}
                                        showPastDates={false}
                                        selectedDate={selectedDate}
                                        onSelectDate={(date) => {
                                            setSelectedDate(date);
                                            scrollTo(hours);
                                        }}
                                        onChangeTimespan={(month, year) => {
                                            setMonth(month);
                                            setYear(year);
                                        }}
                                        disabledDates={disabledDates}
                                        disabledDays={disabled_days}
                                        maxDate={futureBooking ? moment(futureBooking * 1000).toDate() : null}
                                    />
                                </>
                            )}
                        </Col>

                        <Col xs={12} md={6} className={'mt-4'}>
                            {selectedDate && (
                                <div ref={hours} className={'d-flex flex-column h-100'}>
                                    <span className={[
                                        Styles.label,
                                        availableTimeList && availableTimeList.length === 0 ? 'text-danger' : undefined
                                    ].join(' ')}>
                                        {t(availableTimeList && availableTimeList.length === 0 ? 'bookingScreen.noAvailableHours' : 'bookingScreen.availableHours')}
                                    </span>

                                    {!availableTimeList && (
                                        <Loader
                                            className="d-block text-center"
                                            type="TailSpin"
                                            color="#000"
                                            height={32}
                                            width={32}
                                        />
                                    )}

                                    {availableTimeList && availableTimeList.length !== 0 && (
                                        <div className={Styles.hours}>
                                            {availableTimeList.map((timestamp, i) => {
                                                const time = moment(timestamp * 1000).utc().format('HH:mm');
                                                return (
                                                <button key={`hour-${i}`} className={[
                                                    Styles.hour,
                                                    timestamp === selectedHour ? Styles.selected : undefined
                                                ].join(' ')}
                                                data-testid={`data-test-booking-hour-button-${time}`}
                                                        onClick={() => dispatch(actions.selectTimestamp(timestamp))}>
                                                    {time}
                                                </button>
                                            )})}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>

            <ScreenFooter visible={!!selectedHour}>
                <Button 
                    variant={'tint'} size={'lg'} 
                    className={Styles.continueButton}
                    onClick={() => push(ROUTER_URL.BOOK_CONFIRM_SERVICE, {
                            timestamp: selectedHour,
                            productID: selectedProductID
                        })}
                    data-testid={'continue-button'}
                >
                    {t('bookingScreen.continue')}
                </Button>
            </ScreenFooter>
        </>
    )
};

export default Booking
